import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { HiOutlineCode, HiOutlineSearch, HiOutlineViewList, HiOutlineX } from "react-icons/hi";
import API from "../../api";
import { capture } from "../../instrument";
import { AUDIT_COLLECTION_LABELS, auditActorFilterParams, auditDistinctActors } from "../../utils";
import DateTimeInput from "../DateTimeInput";
import ActorChips from "./ActorChips";
import AuditTimeline from "./AuditTimeline";
import { auditSearchParams } from "./labels";

const PRESETS = [
  { key: "24h", label: "24 heures", ms: 24 * 36e5 },
  { key: "7d", label: "7 jours", ms: 7 * 24 * 36e5 },
  { key: "30d", label: "30 jours", ms: 30 * 24 * 36e5 },
  { key: "all", label: "Tout", ms: null },
  { key: "custom", label: "Personnalisé", ms: null },
];

const ACTION_FILTERS = [
  { key: "UPDATE", label: "Modifications" },
  { key: "CREATE", label: "Créations" },
  { key: "DELETE", label: "Suppressions" },
  { key: "LOGIN,LOGIN_FAILED,LOGIN_LOCKED,PASSWORD_RESET,PASSWORD_RESET_REQUEST,OTP_SENT_SMS,OTP_SENT_EMAIL,OTP_DISABLED", label: "Connexions" },
  { key: "EMAIL_SENT", label: "Emails" },
  { key: "PROCESSING", label: "Traitements robot" },
  { key: "ACTIONS_APPLIED", label: "Actions appliquées" },
  {
    key: "SYNC,SYNC_INGEST,SYNC_RECONCILE,SYNC_CONFLICT_RESOLVED,SYNC_OVERRIDE_REMOVED,SYNC_RUN_REQUESTED,SYNC_RUN_FAILED,SYNC_CURSOR_RESET,REORDER,EXPORT_CONFIG,IMPORT_CONFIG",
    label: "Configuration",
  },
];

const buildParams = ({ q, actor, collectionName, documentId, action, preset, customStart, customEnd, cursor }) => {
  const params = new URLSearchParams({ limit: "50", ...auditActorFilterParams(actor), ...(q && auditSearchParams(q)) });
  if (documentId && collectionName === "admissions") {
    params.append("admissionId", documentId);
  } else {
    if (documentId) params.append("documentId", documentId);
    if (collectionName) params.append("collectionName", collectionName);
  }
  if (q) params.append("q", q);
  if (action) params.append("action", action);
  if (preset === "custom") {
    if (customStart) params.append("from", new Date(customStart).toISOString());
    if (customEnd) params.append("to", new Date(customEnd).toISOString());
  } else {
    const ms = PRESETS.find((item) => item.key === preset)?.ms;
    if (ms) params.append("from", new Date(Date.now() - ms).toISOString());
  }
  if (cursor) params.append("cursor", cursor);
  return params;
};

const AuditJournal = ({ collectionName, documentId, user }) => {
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [actor, setActor] = useState("");
  const [actors, setActors] = useState([]);
  const [collectionFilter, setCollectionFilter] = useState("");
  const [action, setAction] = useState("");
  const [preset, setPreset] = useState(documentId ? "all" : "7d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [isRawView, setIsRawView] = useState(false);
  const canViewRaw = user.role === "superadmin";

  useEffect(() => {
    const timeout = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    setCollectionFilter("");
    setActor("");
    setPreset(documentId ? "all" : "7d");
  }, [collectionName, documentId]);

  const filters = { q, actor, collectionName: collectionName || collectionFilter, documentId, action, preset, customStart, customEnd };

  const { data: allActors = [] } = useQuery({
    queryKey: ["audit-actors"],
    queryFn: async () => {
      const response = await API.get("/audit-log/actors");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    enabled: !documentId,
  });

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["audit-events", filters],
    queryFn: async ({ pageParam }) => {
      const response = await API.get(`/audit-log?${buildParams({ ...filters, cursor: pageParam })}`);
      if (!response.ok) {
        const error = new Error(response.message);
        capture(error);
        throw error;
      }
      return response.data;
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    refetchOnMount: "always",
  });

  const events = useMemo(() => (data?.pages ?? []).flatMap((page) => page.events), [data]);

  useEffect(() => {
    if (!actor && events.length > 0) setActors(auditDistinctActors(events));
  }, [actor, events]);

  const hasFilters = Boolean(q || actor || action || collectionFilter);
  const subject = documentId && collectionName !== "admissions" ? events.find((event) => event.documentId === documentId && event.subject)?.subject : undefined;

  return (
    <div className="flex flex-col gap-4">
      {subject && <h2 className="truncate text-lg font-semibold text-gray-900">{subject}</h2>}

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
        <div className="relative min-w-[220px] flex-1">
          <HiOutlineSearch className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            autoFocus={!documentId}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={documentId ? "Rechercher un champ, une valeur, un intervenant..." : "Rechercher un dossier, un patient, un champ, une valeur..."}
            className="ghost-input pl-8 pr-8"
          />
          {search && (
            <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <HiOutlineX className="h-4 w-4" />
            </button>
          )}
        </div>
        {!collectionName && (
          <select value={collectionFilter} onChange={(e) => setCollectionFilter(e.target.value)} className="ghost-input w-auto text-gray-600">
            <option value="">Quoi</option>
            {Object.entries(AUDIT_COLLECTION_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        )}
        <select value={action} onChange={(e) => setAction(e.target.value)} className="ghost-input w-auto text-gray-600">
          <option value="">Type</option>
          {ACTION_FILTERS.map((item) => (
            <option key={item.key} value={item.key}>
              {item.label}
            </option>
          ))}
        </select>
        <select value={preset} onChange={(e) => setPreset(e.target.value)} className="ghost-input w-auto text-gray-600">
          {PRESETS.map((item) => (
            <option key={item.key} value={item.key}>
              {item.label}
            </option>
          ))}
        </select>
        {preset === "custom" && (
          <>
            <DateTimeInput value={customStart} onChange={setCustomStart} className="ghost-input w-auto" />
            <span className="text-xs text-gray-400">au</span>
            <DateTimeInput value={customEnd} onChange={setCustomEnd} className="ghost-input w-auto" />
          </>
        )}
        {canViewRaw && (
          <div className="ml-auto flex shrink-0 overflow-hidden rounded-md border border-gray-200">
            <button
              type="button"
              onClick={() => setIsRawView(false)}
              title="Vue lisible"
              className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium transition ${!isRawView ? "bg-primary-500 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
            >
              <HiOutlineViewList className="h-3.5 w-3.5" /> Lisible
            </button>
            <button
              type="button"
              onClick={() => setIsRawView(true)}
              title="Vue technique : événements bruts, sans regroupement"
              className={`flex items-center gap-1 border-l border-gray-200 px-2.5 py-1 text-xs font-medium transition ${isRawView ? "bg-primary-500 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
            >
              <HiOutlineCode className="h-3.5 w-3.5" /> Technique
            </button>
          </div>
        )}
      </div>

      {actors.length > 0 && <ActorChips actors={actors} value={actor} onChange={setActor} allActors={allActors} />}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
        </div>
      ) : isError ? (
        <div className="py-16 text-center text-sm text-red-600">Impossible de charger le journal</div>
      ) : events.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 py-10 text-center text-sm text-gray-400">
          {hasFilters ? "Aucun résultat pour ces filtres" : "Aucune activité sur cette période"}
        </div>
      ) : (
        <AuditTimeline
          events={events}
          raw={isRawView}
          showSubject={() => !documentId}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={fetchNextPage}
        />
      )}
    </div>
  );
};

export default AuditJournal;
